import { TTest } from '@datatype/iyagigo/type';
import { EApiTest } from '@datatype/iyagigo/api-type';
import {
  EVocabularyGrade,
  TTestCode,
  TClozeType,
  EApiLanguageCode,
  IStudyConfigurationData,
} from '@datatype/iyagigo/api-type';

// ###################################
// 라우트/메뉴의 소문자 subject를 API의 TClozeType으로 변환
// - 'vocabulary'/'listening' 외에는 모두 'GRAMMAR'로 폴백 (기존 동작 유지)
// ###################################
export const getClozeTypeBySubject = (subject: string | null | undefined): TClozeType => {
  switch (subject) {
    case 'vocabulary':
      return 'VOCABULARY';
    case 'listening':
      return 'LISTENING';
    default:
      return 'GRAMMAR';
  }
};

// ###################################
// 언어 + subject에 해당하는 일일목표(ClozeGoal) 조회
// - 필드명 규칙: {english|japanese}{Vocabulary|Grammar|Listening}ClozeGoal
// - studyConfiguration이 없으면 0
// ###################################
export const getClozeGoal = (
  studyConfiguration: IStudyConfigurationData | undefined | null,
  studyLanguage: EApiLanguageCode | null,
  subject: string | null | undefined,
): number => {
  const CLOZE_GOAL_FIELD: Record<'english' | 'japanese', Record<TClozeType, keyof IStudyConfigurationData>> = {
    english: {
      VOCABULARY: 'englishVocabularyClozeGoal',
      GRAMMAR: 'englishGrammarClozeGoal',
      LISTENING: 'englishListeningClozeGoal',
    },
    japanese: {
      VOCABULARY: 'japaneseVocabularyClozeGoal',
      GRAMMAR: 'japaneseGrammarClozeGoal',
      LISTENING: 'japaneseListeningClozeGoal',
    },
  };

  if (!studyConfiguration) return 0;
  const language = studyLanguage === EApiLanguageCode.ENGLISH ? 'english' : 'japanese';
  const field = CLOZE_GOAL_FIELD[language][getClozeTypeBySubject(subject)];
  return (studyConfiguration[field] as number) ?? 0;
};

// ###################################
// text의 특수기호/공백 제거 및 소문자 변환
// ###################################
export const sanitizeSentence = (text: string): string => {
  return text
    .replace(/[。、？！,.?!]/g, '') // 특수문제 제거
    .replace(/\s+/g, '') // 공백제거
    .toLowerCase() // 소문자 변환ㅊ
    .trim();
};

// ###################################
// 전체문장과 보기를 기반으로, 알맞은 보기 생성 (Backtraking 알고리즘)
// - sentence = "彼が来るか来ないかわかりません";
// - choice = ["彼が", "来る", "か", "来ない", "わかりません"];
// - parsed = ["彼が", "来る", "か", "来ない", "か", "わかりません"]
// - 보기는 특수문자 및 공백 제거
// ###################################
export const parseSentenceWithChoice = (answer: string, choice: string[]): string[] => {
  const parsed: string[] = [];
  const sanitizedAnswer = sanitizeSentence(answer);
  const sanitizedChoice = choice.map((word) => sanitizeSentence(word));

  const backtrack = (remainded: string): boolean => {
    if (remainded === '') return true;

    // choice의 각 단어를 시도
    for (const word of sanitizedChoice) {
      if (remainded.startsWith(word)) {
        // 선택: 단어를 결과에 추가
        parsed.push(word);

        // 재귀: 남은 문자열로 계속 탐색
        if (backtrack(remainded.slice(word.length))) {
          return true; // 성공하면 즉시 반환
        }

        // 되돌리기: 이 선택이 실패하면 다시 빼고 다른 단어 시도
        parsed.pop();
      }
    }

    // 모든 단어를 시도했지만 실패
    return false;
  };

  if (backtrack(sanitizedAnswer)) return parsed;
  return sanitizedChoice;
};

// ###################################
// 오늘 학습하였는지 판단
// - todayStudiedAt: ISO8601("2026-01-11T07:07:23.93513Z)
// ###################################
export const getIsTodayStudied = (todayStudiedAt: string) => {
  const now = new Date();
  const localToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // 로컬 오늘 00:00:00
  const localStudiedAt = new Date(todayStudiedAt); // UTC를 로컬로 자동 변환
  return localStudiedAt >= localToday; // 로컬 기준으로 비교
};

// ###################################
// 오늘 만들어졌는지 판단
// - createdAt: ISO8601("2026-01-11T07:07:23.93513Z)
// ###################################
export const getIsTodayCreated = (created: string) => {
  const now = new Date();
  const localToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // 로컬 오늘 00:00:00
  const localCreatedAt = new Date(created); // UTC를 로컬로 자동 변환
  return localCreatedAt >= localToday; // 로컬 기준으로 비교
};

// ###################################
// 날짜 차이 구하기 (일 단위)
// - targetDate: ISO8601("2026-01-11T07:07:23.93513Z)
// ###################################
export const getDiffDays = (targetDate: string) => {
  const now = new Date();
  const localToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const localTarget = new Date(targetDate);
  const localTargetDate = new Date(localTarget.getFullYear(), localTarget.getMonth(), localTarget.getDate());

  const diffTime = localToday.getTime() - localTargetDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

// ###################################
// Cloze 다음 복습까지의 지연 계산 (밀리초)
// - 백엔드 Workbook.calculateReviewAt 로직을 프론트로 이관한 것 (간격 정책의 진실 공급원)
// - 결과를 IModifyWorkbookRequest.reviewAfter로 보내면 백엔드가 now + reviewAfter로 저장
// - 오답: 1일 / 정답(복습): <10s 10일, <20s 7일, else 3일 / 정답(일반): <10s 5일, <20s 3일, else 1일
// - studyTime: 밀리초, isReview: 다시보기 누적 학습 여부(reviewAt이 이미 있는 문제)
// ###################################
export const DAY_MS = 24 * 60 * 60 * 1000;

export const calculateClozeReviewAfter = (isCorrect: boolean, studyTime: number, isReview: boolean): number => {
  if (!isCorrect) return DAY_MS;

  const studyTimeInSeconds = studyTime / 1000;
  if (isReview) {
    if (studyTimeInSeconds < 10) return 10 * DAY_MS;
    if (studyTimeInSeconds < 20) return 7 * DAY_MS;
    return 3 * DAY_MS;
  }
  if (studyTimeInSeconds < 10) return 5 * DAY_MS;
  if (studyTimeInSeconds < 20) return 3 * DAY_MS;
  return DAY_MS;
};

// ###################################
// 단어집 다음 복습까지의 지연 계산 (밀리초)
// - 백엔드 VocabularyBookBundleWorkbook.recordGrade 로직을 프론트로 이관한 것
// - grade별 고정 간격: HARD 당일(0) / NORMAL 3일 / EASY 7일
// - 결과를 IModifyVocabularyWorkbookRequest.reviewAfter로 보내면 백엔드가 now + reviewAfter로 저장
// ###################################
export const calculateVocabularyBookReviewAfter = (grade: EVocabularyGrade): number => {
  switch (grade) {
    case EVocabularyGrade.HARD:
      return 0;
    case EVocabularyGrade.NORMAL:
      return 3 * DAY_MS;
    case EVocabularyGrade.EASY:
      return 7 * DAY_MS;
    default:
      return 0;
  }
};

// ###################################
// sub menu에서 test code 추출
// ###################################

export const getTestCodeFromCurrentStudy = (currentStudy: TTest | null): TTestCode => {
  let testCode: TTestCode;

  switch (currentStudy) {
    case EApiTest.TOEIC950:
      testCode = 'T2100';
      break;
    case EApiTest.TOEIC850:
      testCode = 'T2200';
      break;
    case EApiTest.TOEIC750:
      testCode = 'T2300';
      break;
    case EApiTest.TOEIC650:
      testCode = 'T2400';
      break;
    case EApiTest.TOEIC550:
      testCode = 'T2500';
      break;
    case EApiTest.JLPT1:
      testCode = 'T1100';
      break;
    case EApiTest.JLPT2:
      testCode = 'T1200';
      break;
    case EApiTest.JLPT3:
      testCode = 'T1300';
      break;
    case EApiTest.JLPT4:
      testCode = 'T1400';
      break;
    case EApiTest.JLPT5:
      testCode = 'T1500';
      break;
    default:
      // testCode = '';
      throw new Error('invalid test');
  }

  return testCode;
};

// export const getTestCodeFromTestName = (
//   testName:
//     | 'toeic550'
//     | 'toeic650'
//     | 'toeic750'
//     | 'toeic850'
//     | 'toeic950'
//     | 'jlpt1'
//     | 'jlpt2'
//     | 'jlpt3'
//     | 'jlpt4'
//     | 'jlpt5',
// ): TTestCode => {
//   let testCode: any;
//
//   switch (subMenu) {
//     case 'toeic950':
//       testCode = 'T2100';
//       break;
//     case 'toeic850':
//       testCode = 'T2200';
//       break;
//     case 'toeic750':
//       testCode = 'T2300';
//       break;
//     case 'toeic650':
//       testCode = 'T2400';
//       break;
//     case 'toeic550':
//       testCode = 'T2500';
//       break;
//     case 'jlpt1':
//       testCode = 'T1100';
//       break;
//     case 'jlpt2':
//       testCode = 'T1200';
//       break;
//     case 'jlpt3':
//       testCode = 'T1300';
//       break;
//     case 'jlpt4':
//       testCode = 'T1400';
//       break;
//     case 'jlpt5':
//       testCode = 'T1500';
//       break;
//     default:
//       testCode = '';
//     // throw new Error('invalid sub menu');
//   }
//
//   return testCode;
// };
